import { cleanupDeclaredStorage } from "./cookie-cleanup.js";
import { globalPrivacyControlEnabled } from "./global-privacy-control.js";
import { BrowserPreferenceStore } from "./preference-store.js";
import type {
  ChangeResult,
  DecisionBasis,
  PreferenceStore,
  PrivacyService,
  PrivacySnapshot,
  PrivacyState,
  ServiceState,
  SitePrivacyConfig,
  StoredDecision,
  StoredPreferences,
} from "./types.js";
import { validateConfig } from "./validate-config.js";

export interface PrivacyControllerOptions {
  store?: PreferenceStore;
  gpc?: () => boolean;
  now?: () => Date;
}

function stableFingerprint(service: PrivacyService): string {
  const cookies = service.cookies
    .map((cookie) => (typeof cookie === "string" ? cookie : JSON.stringify(cookie)))
    .sort();
  const material = JSON.stringify({
    category: service.category,
    provider: service.provider ?? "",
    cookies,
    storageKeys: [...service.storageKeys].sort(),
    requestDomains: [...service.requestDomains].sort(),
  });
  let hash = 2166136261;
  for (let index = 0; index < material.length; index += 1) {
    hash ^= material.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function validDate(value: string): number | null {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : null;
}

function isDecision(value: unknown): value is StoredDecision {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<StoredDecision>;
  return (
    (item.state === "on" || item.state === "off") &&
    ["consent", "refusal", "objection", "gpc"].includes(item.basis ?? "") &&
    typeof item.purposeFingerprint === "string"
  );
}

export class PrivacyController {
  private readonly store: PreferenceStore;
  private readonly gpc: () => boolean;
  private readonly now: () => Date;
  private readonly listeners = new Set<(snapshot: PrivacySnapshot) => void>();
  private readonly loaded = new Set<string>();
  private operation = Promise.resolve();
  private unsubscribeStore: (() => void) | undefined;

  constructor(
    readonly config: SitePrivacyConfig,
    options: PrivacyControllerOptions = {},
  ) {
    validateConfig(config);
    this.store = options.store ?? new BrowserPreferenceStore();
    this.gpc = options.gpc ?? globalPrivacyControlEnabled;
    this.now = options.now ?? (() => new Date());
    this.unsubscribeStore = this.store.subscribe?.(() => {
      void this.enqueue(async () => {
        await this.reconcile();
        this.emit();
      });
    });
  }

  async initialize(): Promise<PrivacySnapshot> {
    return this.enqueue(async () => {
      await this.reconcile();
      const snapshot = this.snapshot();
      this.emit(snapshot);
      return snapshot;
    });
  }

  snapshot(): PrivacySnapshot {
    const stored = this.currentStoredPreferences();
    return {
      mode: this.config.mode,
      gpc: this.gpc(),
      services: this.config.services.map((service) => this.resolveService(service, stored)),
    };
  }

  subscribe(listener: (snapshot: PrivacySnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  async setServiceState(serviceId: string, state: PrivacyState): Promise<ChangeResult> {
    const service = this.config.services.find((candidate) => candidate.id === serviceId);
    if (!service) throw new Error(`Unknown privacy service '${serviceId}'`);
    if (service.category === "essential" && state === "off") {
      throw new Error(`Essential service '${serviceId}' cannot be disabled`);
    }
    if (service.category !== "essential" && this.config.mode === "essential-only" && state === "on") {
      throw new Error("Optional services cannot be enabled in essential-only mode");
    }
    if (service.category !== "essential" && this.gpc() && state === "on") {
      throw new Error("Optional services cannot be enabled while Global Privacy Control is active");
    }
    return this.setOptionalStates({ [serviceId]: state });
  }

  async setAllOptional(state: PrivacyState): Promise<ChangeResult> {
    const choices = Object.fromEntries(
      this.config.services
        .filter((service) => service.category !== "essential")
        .map((service) => [service.id, state]),
    );
    return this.setOptionalStates(choices);
  }

  destroy(): void {
    this.unsubscribeStore?.();
    this.unsubscribeStore = undefined;
    this.listeners.clear();
  }

  private async setOptionalStates(choices: Record<string, PrivacyState>): Promise<ChangeResult> {
    return this.enqueue(async () => {
      const now = this.now();
      const existing = this.currentStoredPreferences();
      const decisions: Record<string, StoredDecision> = {};
      if (existing?.siteId === this.config.siteId) {
        for (const [id, decision] of Object.entries(existing.decisions)) {
          if (isDecision(decision)) decisions[id] = decision;
        }
      }

      for (const [id, state] of Object.entries(choices)) {
        const service = this.config.services.find((candidate) => candidate.id === id);
        if (!service) throw new Error(`Unknown privacy service '${id}'`);
        if (service.category === "essential") continue;
        if (state === "on" && this.config.mode === "essential-only") {
          throw new Error("Optional services cannot be enabled in essential-only mode");
        }
        if (state === "on" && this.gpc()) {
          throw new Error("Optional services cannot be enabled while Global Privacy Control is active");
        }
        const basis: DecisionBasis =
          state === "on"
            ? "consent"
            : this.config.mode === "uk-statistics-opt-out"
              ? "objection"
              : "refusal";
        decisions[id] = {
          state,
          basis,
          purposeFingerprint: stableFingerprint(service),
        };
      }

      const expiresAt = new Date(
        now.getTime() + this.config.preferenceLifetimeDays * 86_400_000,
      );
      const preferences: StoredPreferences = {
        schemaVersion: 1,
        siteId: this.config.siteId,
        policyVersion: this.config.policyVersion,
        updatedAt: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        decisions,
      };
      let durable = this.store.write(preferences);
      if (
        !durable &&
        this.config.mode === "prior-consent" &&
        Object.entries(choices).some(([, state]) => state === "on")
      ) {
        for (const [id, state] of Object.entries(choices)) {
          if (state !== "on") continue;
          const service = this.config.services.find((candidate) => candidate.id === id);
          if (!service) continue;
          decisions[id] = {
            state: "off",
            basis: "refusal",
            purposeFingerprint: stableFingerprint(service),
          };
        }
        durable = this.store.write({ ...preferences, decisions });
      }
      const previouslyLoaded = Object.keys(choices).some((id) => this.loaded.has(id));
      await this.reconcile();
      const snapshot = this.snapshot();
      this.emit(snapshot);
      return {
        snapshot,
        durable,
        reloadRecommended:
          previouslyLoaded && Object.values(choices).some((state) => state === "off"),
      };
    });
  }

  private currentStoredPreferences(): StoredPreferences | null {
    const stored = this.store.read();
    if (!stored || stored.siteId !== this.config.siteId) return null;
    const expiry = validDate(stored.expiresAt);
    if (expiry === null || expiry <= this.now().getTime()) return null;
    return stored;
  }

  private resolveService(
    service: PrivacyService,
    stored: StoredPreferences | null,
  ): ServiceState {
    if (service.category === "essential") {
      return { ...this.publicState(service), state: "on", basis: "essential" };
    }
    if (this.gpc()) {
      return { ...this.publicState(service), state: "off", basis: "gpc" };
    }

    const decision = stored?.decisions[service.id];
    if (isDecision(decision)) {
      if (decision.state === "off") {
        return { ...this.publicState(service), state: "off", basis: decision.basis };
      }
      const positiveStillValid =
        stored?.policyVersion === this.config.policyVersion &&
        decision.purposeFingerprint === stableFingerprint(service);
      if (positiveStillValid) {
        return { ...this.publicState(service), state: "on", basis: decision.basis };
      }
    }

    const ukStatisticsDefault =
      this.config.mode === "uk-statistics-opt-out" &&
      this.config.audience === "uk" &&
      service.category === "statistics" &&
      service.defaultState === "on";
    return {
      ...this.publicState(service),
      state: ukStatisticsDefault ? "on" : "off",
      basis: ukStatisticsDefault ? "default" : "fail-closed",
    };
  }

  private publicState(service: PrivacyService): Pick<ServiceState, "id" | "displayName" | "category"> {
    return {
      id: service.id,
      displayName: service.displayName,
      category: service.category,
    };
  }

  private async reconcile(): Promise<void> {
    const snapshot = this.snapshot();
    for (const state of snapshot.services) {
      const service = this.config.services.find((candidate) => candidate.id === state.id);
      if (!service) continue;
      if (state.state === "on" && !this.loaded.has(service.id)) {
        await service.load();
        this.loaded.add(service.id);
      } else if (state.state === "off" && this.loaded.has(service.id)) {
        await service.disable?.();
        cleanupDeclaredStorage(service);
        this.loaded.delete(service.id);
      }
    }
  }

  private emit(snapshot = this.snapshot()): void {
    for (const listener of this.listeners) listener(snapshot);
  }

  private enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.operation.then(operation, operation);
    this.operation = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }
}

export function createPrivacyController(
  config: SitePrivacyConfig,
  options?: PrivacyControllerOptions,
): PrivacyController {
  return new PrivacyController(config, options);
}
